import React from 'react';
import styled from 'styled-components';
import { Typography, Carousel } from '@vp/react-vp-ui-library';
import { useMappedState } from 'redux-react-hook';
import { getLocalizedText as t } from '@vp-dst/localization-util';

import withScreenClass, { WrappedScreenClassProps } from '../highOrderComponents/withScreenClass';
import ProductSlide from './ProductSlide';
import { LoadablePrice } from './Price';
import { State } from '../state/reducer';
import { selectProducts } from '../state/selectors';

const UploadAreaWrapper = styled.div`
  background-color: rgb(243, 243, 243);
  padding: 40px 0 40px 0;

  .carousel-slide:focus {
    outline: none;
  }
`;

const CenteredText = styled(Typography)`
  text-align: center;
`;

// Pixel widths of the window where we change the number of slides to show.
// If the window is narrow than the first breakpoint we show one slide;
// if it is narrow that the second breakpoint we show two slides, etc.
const SLIDES_SHOWN_BREAKPOINTS = [590, 760, 1000, 1200];

const mapState = (state: State) => {
  // we assume that all loadables will be defined in this component
  // the top-level component won't render this until all loadables are loaded
  // thus it's safe for us to us the non-null assertion (!)

  return {
    baseOffer: state.loadableBaseOffer!.data!,
    products: selectProducts(state),
  };
};

function UploadArea(props: WrappedScreenClassProps & {}) {
  const { screenClass } = props;

  const { baseOffer, products } = useMappedState(mapState);

  // screenClasses were set such that they fit n + 1 cards
  const slidesToShow = Math.min(screenClass + 1, products.length);

  // sort by display-order-weight
  const sortedProducts = products.sort((a, b) => {
    return a.displayOrderWeight - b.displayOrderWeight;
  });

  return (
    <UploadAreaWrapper>
      <CenteredText size={2} strong={true} purpose="heading">
        {t('select-the-shape-of-your-business-card')}
      </CenteredText>
      <CenteredText size={4} purpose="heading">
        <LoadablePrice price={{ __html: baseOffer.presentationHTML }} />
      </CenteredText>
      <div style={{ paddingTop: 20 }}>
        <Carousel
          arrows={true}
          dots={false} // when the screen is very small, the dots don't look good. So we're disabling them
          alignSlides="left"
          slidesToShow={slidesToShow}
          slidesToScroll={slidesToShow}
        >
          {sortedProducts.map((product, index) => (
            <ProductSlide key={`slide-${index}`} product={product} />
          ))}
        </Carousel>
      </div>
    </UploadAreaWrapper>
  );
}

export default withScreenClass(SLIDES_SHOWN_BREAKPOINTS)(UploadArea);
